import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    output,
    signal,
    ViewEncapsulation,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { IngestionFieldDefinition } from "../data-ingestion/data.ingestion.service";
import { WorkflowUtil } from "./workflow.util";
import { Bind } from "../../decorators/bind.decorator";
import { AnswerType } from "../data-ingestion/data.ingestion.service";

@Component({
    standalone: true,
    selector: "workflow-field-reference-helper",
    template: `
        <select [(ngModel)]="selectedFieldReference" class="form-control">
            <option value="">Select field to copy reference...</option>
            @for (field of referenceableFields(); track field.fieldDefinitionId) {
                <option [value]="getFieldReference(field)">
                    {{ field.fieldLabel }}{{ field.answerType ? " (" + answerTypeLabel(field.answerType) + ")" : "" }}
                </option>
            }
        </select>
        <small class="form-text">
            {{ helpText() }}
        </small>
        @if (fieldReferenceMessage()) {
            <div
                class="field-reference-message"
                [class.success]="fieldReferenceMessage().type === 'success'"
                [class.error]="fieldReferenceMessage().type === 'error'"
            >
                {{ fieldReferenceMessage().text }}
                @if (fieldReferenceMessage().fieldReference) {
                    <br /><strong>Field Reference:</strong>
                    {{ fieldReferenceMessage().fieldReference }}
                }
            </div>
        }
    `,
    styles: `
        :host {
            display: block;
            border: 1px solid var(--color--link-text);
            border-radius: 6px;
            padding: 12px;
            background: var(--color--surface-2-bg);
        }

        label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: var(--color--text-strong);
        }

        select {
            margin-bottom: 8px;
        }

        .form-text {
            font-size: 11px;
            color: var(--color--text-muted);
            margin-top: 4px;
            display: block;
        }

        .field-reference-message {
            margin-top: 8px;
            padding: 8px 12px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;
        }

        .field-reference-message.success {
            background: var(--color--registration);
            color: white;
            border: 1px solid var(--color--registration);
        }

        .field-reference-message.error {
            background: var(--color--danger);
            color: white;
            border: 1px solid var(--color--danger);
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [FormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowFieldReferenceHelperComponent {
    formFields = input.required<IngestionFieldDefinition[]>();
    readonly referenceableFields = computed(() =>
        this.formFields().filter((f) => f.fieldType !== "SECTION" && f.fieldType !== "FORM"),
    );
    helpText = input<string>(
        "Select a field to copy its reference (e.g., $CNET-123-FIELD_CODE) to clipboard for pasting into inputs below.",
    );

    fieldReferenceCopied = output<string>();

    selectedFieldReference = signal<string>(undefined);
    readonly fieldReferenceMessage = signal<{ type: "success" | "error"; text: string; fieldReference?: string }>(
        undefined,
    );

    constructor() {
        effect(this.onFieldReferenceChange);
    }

    /**
     * Handle field reference selection - copy to clipboard and reset dropdown
     */
    @Bind
    onFieldReferenceChange(): void {
        const fieldReference = this.selectedFieldReference();
        if (!fieldReference) return;

        // Check if clipboard API is available (requires HTTPS)
        if (navigator.clipboard && window.isSecureContext) {
            // Copy to clipboard
            navigator.clipboard
                .writeText(fieldReference)
                .then(() => {
                    this.fieldReferenceMessage.set({
                        type: "success",
                        text: "Field reference copied to clipboard!",
                        fieldReference: fieldReference,
                    });
                    console.log("Field reference copied to clipboard:", fieldReference);
                    this.fieldReferenceCopied.emit(fieldReference);
                })
                .catch((err) => {
                    console.error("Failed to copy field reference to clipboard:", err);
                    this.fieldReferenceMessage.set({
                        type: "error",
                        text: "Failed to copy to clipboard. Field reference:",
                        fieldReference: fieldReference,
                    });
                });
        } else {
            // Fallback for non-HTTPS environments
            this.fieldReferenceMessage.set({
                type: "error",
                text: "Clipboard API not available. Field reference:",
                fieldReference: fieldReference,
            });
        }

        // Clear the selection after a delay
        setTimeout(() => {
            this.selectedFieldReference.set(undefined);
            this.fieldReferenceMessage.set(undefined);
        }, 10_000);
    }

    getFieldReference(field: IngestionFieldDefinition): string {
        const fieldCode = WorkflowUtil.getFieldCode(field);
        return `$CNET-${field.fieldDefinitionId}-${fieldCode}`;
    }
    answerTypeLabel(type: AnswerType) {
        return WorkflowUtil.answerTypeLabel(type);
    }
}
